import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { reportData } from '../data/reportData';
import SEO from '../components/SEO';
import DataVizBar from '../components/DataVizBar';
import RiskBadge from '../components/RiskBadge';
import CTABlock from '../components/CTABlock';
import { FiArrowLeft, FiAlertCircle, FiTrendingUp } from 'react-icons/fi';

export default function Question() {
  const { slug } = useParams();
  
  const questionData = reportData.questions[slug];
  if (!questionData) return <Navigate to="/shadow-ai-benchmark-report-2026" />;

  const parentSection = reportData.sections.find(s => s.questions.includes(slug));
  
  const relatedQuestions = questionData.related.map(qId => ({
    id: qId,
    title: reportData.questions[qId].title
  }));

  return (
    <div className="bg-white min-h-screen pb-20">
      <SEO title={questionData.title} description={questionData.hook} />
      
      {/* Breadcrumbs */}
      <div className="border-b border-slate-100 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex flex-wrap gap-2 text-sm font-medium text-slate-500">
          <Link to="/shadow-ai-benchmark-report-2026" className="hover:text-slate-900">Report Home</Link>
          <span>/</span>
          {parentSection && (
            <>
              <Link to={`/${parentSection.slug}`} className="hover:text-slate-900">{parentSection.title}</Link>
              <span>/</span>
            </>
          )}
          <span className="text-slate-900 truncate">Data Point</span>
        </div>
      </div>

      <article className="max-w-4xl mx-auto px-4 pt-12 md:pt-16">
        
        {/* Header Area */}
        <header className="mb-12">
          <h1 className="text-3xl md:text-5xl font-extrabold text-slate-900 leading-tight mb-8">
            {questionData.title}
          </h1>
          <div className="text-xl md:text-2xl text-slate-600 font-light leading-relaxed border-l-4 border-slate-200 pl-6 py-2">
            "{questionData.hook}"
          </div>
        </header>

        {/* Data Visualization */}
        <div className="bg-slate-50 rounded-2xl p-8 mb-12 border border-slate-100">
          <div className="flex items-center gap-2 mb-6">
            <FiTrendingUp className="text-slate-400" />
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500">Benchmark Data</h3>
          </div>
          <DataVizBar data={questionData.data} />
        </div>

        {/* Content Body */}
        <div className="prose prose-lg prose-slate max-w-none mb-16">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Benchmark Insight</h2>
          <p className="text-slate-700 leading-relaxed mb-8">
            {questionData.insight}
          </p>

          <div className="bg-slate-900 text-white rounded-xl p-8 shadow-lg my-10 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-2 h-full bg-peridot-500"></div>
            <h2 className="text-xl font-bold text-white mb-4 mt-0">What This Means For You</h2>
            <p className="text-slate-300 leading-relaxed m-0 text-lg">
              {questionData.whatThisMeans}
            </p>
          </div>

          {/* Risk Signal */}
          <div className="border border-slate-200 rounded-xl p-6 bg-white flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <div className="shrink-0">
              <RiskBadge level={questionData.riskLevel} />
            </div>
            <p className="text-slate-700 font-medium m-0 flex-grow">
              <span className="text-slate-900 font-bold mr-2">Risk Signal:</span>
              {questionData.riskText}
            </p>
          </div>
        </div>

        <hr className="border-slate-200 my-16" />

        {/* Internal Linking & Conversion Engine */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mb-16">
          <div className="lg:col-span-2">
            <h3 className="text-xl font-bold text-slate-900 mb-6">Related Insights</h3>
            <div className="space-y-4">
              {relatedQuestions.map(rq => (
                <Link key={rq.id} to={`/${rq.id}`} className="block group">
                  <div className="p-4 rounded-lg border border-slate-200 hover:border-slate-400 hover:bg-slate-50 transition-colors flex justify-between items-center">
                    <span className="font-semibold text-slate-700 group-hover:text-slate-900">{rq.title}</span>
                    <FiArrowLeft className="rotate-180 text-slate-400 group-hover:text-slate-900 transition-colors" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Massive CTA */}
        <CTABlock variant="dark" />
        
      </article>
    </div>
  );
}